import React from 'react';
import { Droplets, Minus, Plus } from 'lucide-react';

export interface HumiditySettingStyle02Props {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: number) => void;
  label?: string;
  currentHumidity?: number;
}

/**
 * HumiditySetting Style 02 - Stepper
 * Command: HumidityRelative
 * Plus/minus stepper buttons for precise control
 */
export const Style02_Stepper: React.FC<HumiditySettingStyle02Props> = ({
  value,
  min = 40,
  max = 70,
  step = 5,
  disabled = false,
  loading = false,
  onChange,
  label = '目標濕度',
  currentHumidity
}) => {
  const handleDecrement = () => {
    const newValue = Math.max(min, value - step);
    onChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(max, value + step);
    onChange(newValue);
  };

  return (
    <div className="space-y-4">
      {/* Current humidity display */}
      {currentHumidity !== undefined && (
        <div className="flex items-center justify-center gap-2 p-3 bg-blue-500/10 rounded-xl">
          <Droplets className="w-5 h-5 text-blue-400" />
          <span className="text-sm text-slate-300">目前室內濕度</span>
          <span className="text-lg font-bold text-blue-400">{currentHumidity}%</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-300">{label}</span>
        <span className="text-sm text-cyan-400">{value}%</span>
      </div>

      {/* Stepper control */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={handleDecrement}
          disabled={disabled || loading || value <= min}
          className="w-12 h-12 rounded-full bg-slate-700/50 hover:bg-slate-600 text-white flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Minus className="w-6 h-6" />
        </button>
        
        <div className="flex flex-col items-center">
          <span className="text-4xl font-bold text-cyan-400">
            {loading ? '...' : value}
          </span>
          <span className="text-lg text-slate-400">%</span>
        </div>
        
        <button
          onClick={handleIncrement}
          disabled={disabled || loading || value >= max}
          className="w-12 h-12 rounded-full bg-slate-700/50 hover:bg-slate-600 text-white flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>

      {/* Range indicator */}
      <div className="flex justify-between text-xs text-slate-500">
        <span>最低 {min}%</span>
        <span>最高 {max}%</span>
      </div>
    </div>
  );
};

export default Style02_Stepper;
